'use client'

import { useEffect, useState } from 'react'
import { AlignCenterIcon, AlignJustifyIcon, AlignLeftIcon, AlignRightIcon } from 'lucide-react'
import { useEditorContext } from '@/components/ui/editor'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const Align = () => {
  const { editor, sourceMode } = useEditorContext()
  const [activeAlign, setActiveAlign] = useState<string | null>(null)

  useEffect(() => {
    if (!editor) {
      return
    }

    const update = () => {
      const types = ['left', 'center', 'right', 'justify'] as const
      const active = types.find(align => editor.isActive({ textAlign: align }))
      setActiveAlign(active ?? null)
    }

    update()

    editor.on('selectionUpdate', update)
    editor.on('transaction', update)

    return () => {
      editor.off('selectionUpdate', update)
      editor.off('transaction', update)
    }
  }, [editor])

  const alignments = [
    {
      icon: <AlignLeftIcon />,
      value: 'left',
      label: 'Align left'
    },
    {
      icon: <AlignCenterIcon />,
      value: 'center',
      label: 'Align center'
    },
    {
      icon: <AlignRightIcon />,
      value: 'right',
      label: 'Align right'
    },
    {
      icon: <AlignJustifyIcon />,
      value: 'justify',
      label: 'Align justify'
    }
  ]

  return (
    <div className="flex items-center gap-1">
      {alignments.map((align, i) => {
        const isActive = activeAlign === align.value

        const handleClick = () => {
          if (isActive) {
            editor.chain().focus().unsetTextAlign().run()
          } else {
            editor.chain().focus().setTextAlign(align.value).run()
          }
        }

        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={isActive ? 'default' : 'ghost'}
                onClick={handleClick}
                title={align.label}
                disabled={sourceMode}
                tabIndex={-1}>
                {align.icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{align.label}</p>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

export default Align
